// src/components/ui/Charts/CustomTooltip.tsx
import React from 'react';
import { formatNumber, formatPercent, formatCurrency, formatCompactNumber } from '@/utils/Formatters';
import styles from './CustomTooltip.module.css';

export interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: any;
    dataKey: string;
    color: string;
    payload: any;
  }>;
  label?: string;
  labelFormatter?: (label: any) => React.ReactNode;
  formatter?: (value: any, name: string, props: any) => React.ReactNode;
  valueFormatter?: (value: any) => string;
  itemSorter?: (a: any, b: any) => number;
  valueType?: 'number' | 'percent' | 'currency' | 'compact';
  precision?: number;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  labelFormatter,
  formatter,
  valueFormatter,
  itemSorter,
  valueType = 'number',
  precision = 2,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  // Форматирование числовых значений для отображения
  const formatValue = (value: any): string => {
    if (valueFormatter) {
      return valueFormatter(value);
    }
    
    // Используем соответствующие функции форматирования в зависимости от типа значения
    if (typeof value === 'number') {
      switch (valueType) {
        case 'percent':
          return formatPercent(value, precision);
        case 'currency':
          // Исправленный вызов - убираем третий параметр
          return formatCurrency(value, '₽');
        case 'compact':
          return formatCompactNumber(value, precision);
        case 'number':
        default:
          return formatNumber(value, precision);
      }
    }
    
    return String(value);
  };

  // Сортировка элементов если указан itemSorter
  const sortedPayload = itemSorter ? [...payload].sort(itemSorter) : payload;

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipLabel}>
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      <div className={styles.tooltipItems}>
        {sortedPayload.map((entry, index) => {
          const displayValue = formatter 
            ? formatter(entry.value, entry.name, entry) 
            : formatValue(entry.value);
            
          return (
            <div key={`tooltip-item-${index}`} className={styles.tooltipItem}>
              <div 
                className={styles.colorSwatch} 
                style={{ backgroundColor: entry.color }}
              ></div>
              <span className={styles.itemName}>{entry.name}:</span>
              <span className={styles.itemValue}>{displayValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};